import {
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    Divider,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { FiArrowUpRight } from "react-icons/fi";
import useWindowSize from "../../../../hooks/useWindowSize";

const headCellSx = {
    fontWeight: 700,
    fontSize: 13,
    color: "#fff",
    backgroundColor: "var(--color-primary-background)",
    whiteSpace: "nowrap",
};

function getInitials(name = "") {
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase();
}

function InfoRow({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={600} noWrap>{value}</Typography>
        </Stack>
    );
}

export default function LeadsList({ clients, openLead }) {
    const width = useWindowSize();
    const isDesktop = width >= 1280;

    if (isDesktop) {
        return (
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 3, border: "1px solid #e3e6ea", maxHeight: "79vh" }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headCellSx, width: 56 }} />
                            <TableCell sx={headCellSx} align="center">Nome</TableCell>
                            <TableCell sx={headCellSx} align="center">Email</TableCell>
                            <TableCell sx={headCellSx} align="center">Telefone</TableCell>
                            <TableCell sx={headCellSx} align="center">CPF</TableCell>
                            <TableCell sx={headCellSx} align="center">Válido</TableCell>
                            <TableCell sx={headCellSx} align="center">Ativo</TableCell>
                            <TableCell sx={headCellSx} align="center">Captado de</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {clients.map((item, index) => {
                            const document = item.document ?? "Sem dados";

                            return (
                                <TableRow
                                    key={item.id ?? index}
                                    hover
                                    sx={{
                                        backgroundColor: index % 2 === 0 ? "#fff" : "#fafbfc",
                                        "&:last-child td": { borderBottom: 0 },
                                    }}
                                >
                                    <TableCell>
                                        <Tooltip title="Abrir lead">
                                            <IconButton
                                                size="small"
                                                onClick={() => openLead(item)}
                                                sx={{
                                                    color: "var(--color-primary-background)",
                                                    border: "1px solid var(--color-primary-background)",
                                                    "&:hover": {
                                                        backgroundColor: "var(--color-primary-background)",
                                                        color: "#fff",
                                                    },
                                                }}
                                            >
                                                <FiArrowUpRight size={16} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    fontSize: 13,
                                                    bgcolor: "var(--color-primary-background)",
                                                }}
                                            >
                                                {getInitials(item.name)}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                                        </Stack>
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">{item.email}</Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2">{item.phone}</Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily: "monospace", textAlign: 'center' }}>{document}</Typography>
                                    </TableCell>

                                    <TableCell align="center">
                                        <Chip label={item.is_valid ? "Sim" : "Não"} color={item.is_valid ? "success" : "error"} size="small" />
                                    </TableCell>

                                    <TableCell align="center">
                                        <Chip label={item.active ? "Sim" : "Não"} color={item.active ? "success" : "error"} size="small" variant="outlined" />
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">{item.capted_from}</Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    return (
        <Stack spacing={2}>
            {clients.map((item, index) => {
                const document = item.document ?? "Sem dados";

                return (
                    <Card key={item.id ?? index} variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                            <Avatar sx={{ bgcolor: "var(--color-primary-background)" }}>
                                {getInitials(item.name)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight={700} noWrap>{item.name}</Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>{item.email}</Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                            <Chip label={item.is_valid ? "Válido" : "Inválido"} color={item.is_valid ? "success" : "error"} size="small" />
                            <Chip label={item.active ? "Ativo" : "Inativo"} color={item.active ? "success" : "error"} size="small" variant="outlined" />
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />

                        <Stack spacing={1}>
                            <InfoRow label="Telefone" value={item.phone} />
                            <InfoRow label="CPF" value={document} />
                            <InfoRow label="Captado de" value={item.capted_from} />
                        </Stack>

                        <Button
                            fullWidth
                            endIcon={<FiArrowUpRight />}
                            onClick={() => openLead(item)}
                            sx={{
                                mt: 2,
                                backgroundColor: "var(--color-primary-background)",
                                color: "#fff",
                                "&:hover": { backgroundColor: "var(--color-secondary-background)" },
                            }}
                        >
                            Abrir lead
                        </Button>
                    </Card>
                );
            })}
        </Stack>
    );
}
